import { Component, OnInit, ElementRef, ViewChild, OnChanges, HostListener, Input, Output, EventEmitter, Inject, DOCUMENT, ChangeDetectionStrategy } from "@angular/core";
import { VideoPlayerBaseComponent } from "@mypxplat/xplat/features";
import {
  ContentService,
  environment,
  EventBusService,
  EventsService,
  HelperService,
  Lesson,
  ProductService,
  StorageKeys,
  StorageService,
  UserService,
  Video,
  WindowService,
  applyReactionLocally,
  normalizeReactions,
  ReactionType,
} from "@mypxplat/xplat/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { filter, take, takeUntil } from "rxjs/operators";
import { AppService, AvoService, BetaService, FirebaseService, WebCommunityService, EmbedBridgeService, EmbedShopContent } from "@mypxplat/xplat/web/core";
import { Location } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { EditCommentComponent, SimpleSignupComponent } from "../modals";
import { ChatComponent } from "../chat/chat.component";
import Player from "@vimeo/player";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpParams } from "@angular/common/http";
declare var YT: any;
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-video-player",
  templateUrl: "video-player.component.html",
  standalone: false,
})
export class VideoPlayerComponent extends VideoPlayerBaseComponent implements OnInit, OnChanges {
  @Output() closed: EventEmitter<boolean> = new EventEmitter();

  /**
   * Embed mode: render the player inline (e.g. inside the fender.com livestream iframe)
   * instead of as a closeable modal. Skips history/back-button, body scroll-lock, and the
   * close/minimize chrome; shows comments only. Set `initialVideo` to drive it directly
   * without going through the global playVideo event bus.
   */
  @Input() embedMode: boolean = false;
  @Input() initialVideo: any = null;

  /** When false (admin disabled comments for this livestream), embed shows video only — no comments UI. */
  @Input() commentsEnabled: boolean = true;

  /**
   * Embed panel features (DENG-893). The comments/shop/sign-up container is generalized to N
   * independently-toggleable tabs; only enabled features render a tab. Shop additionally needs the
   * host to have posted product data (`hasShop`). Sign-up is a click-through to the host page's
   * modal (DENG-892) — the embed only fires the trigger.
   */
  @Input() shopEnabled: boolean = true;
  @Input() signupEnabled: boolean = false;
  /**
   * Per-state sign-up config for the "livestream" state (button_text / form_message / success_message);
   * list_id + source stay server-side. Drives the tab/trigger label and is handed to fui-embed-signup.
   */
  @Input() signupConfig: any = null;
  /** Event id this sign-up belongs to (sent to the capture endpoint). */
  @Input() signupEventId: string = "";
  /**
   * The "countdown" state sign-up config, handed to the pre-show countdown (in-app livestream). Set
   * from the event; the embed drives its countdown's config separately via fui-embed-livestream.
   */
  public signupConfigCountdown: any = null;
  /** When the player opened via "Watch Trailer", tells the countdown to open its trailer modal. */
  public autoOpenTrailer: boolean = false;

  /**
   * Float the comments panel translucently over the video (landscape only; stacks when narrow).
   * Offered for every video via the header icon; defaults ON for the embed, a live stream, and the
   * pre-show countdown, OFF for a recording — and once the viewer picks, their choice is
   * remembered and wins over that default. See `_defaultCommentsOverlay`.
   */
  public commentsOverlay: boolean = false;

  /**
   * Embed shopping rail (Option 1): the host page (fender.com) posts product data in via
   * postMessage; a cart icon in the panel header swaps the panel between Comments and Shop.
   * Only ever populated in embed mode.
   */
  public shopContent: EmbedShopContent | null = null;
  public hasShop: boolean = false;
  private _shopAutoOpened: boolean = false;

  /**
   * Enabled embed panel features, in tab order (DENG-893). Rebuilt whenever a flag or the shop
   * payload changes. Each entry drives a header tab; a feature that's off produces no tab. Never
   * populated outside embed mode.
   */
  public panelFeatures: Array<{ id: string; label: string; icon: string }> = [];
  /** True when at least one embed panel feature is enabled — gates the whole panel/trigger. */
  public hasPanelContent: boolean = false;
  /**
   * True when the in-app (non-embed) player is showing a livestream event, so it uses the same
   * comments/sign-up panel the embed does — respecting the event's comments_enabled and surfacing the
   * Sign-up CTA in MyFender too. (Shop stays embed-only; its rail is posted by the fender.com host.)
   */
  public hasLivestreamPanel: boolean = false;
  /**
   * Minimized-trigger (floating pill) icon + label. Reflects the enabled feature when there's just
   * one, and falls back to a neutral icon/label when several are on — so it's never stuck reading
   * "comments" when comments may be off (DENG-893).
   */
  public panelTriggerIcon: string = "comment";
  public panelTriggerLabel: string = "Show comments";

  /**
   * Remembered overlay-vs-docked choice, or null when the viewer has never picked. Only an
   * explicit toggle writes it; until then each video falls back to the per-type default
   * (`_defaultCommentsOverlay`).
   */
  private _storedCommentsOverlay: boolean | null = null;

  /** Viewer-side toggle to collapse the comments panel in embed mode; a floating button restores it. */
  public commentsHidden: boolean = false;

  /**
   * True when the current video is a livestream event happening RIGHT NOW. In this case we offer
   * the overlay toggle even outside embed mode and default to overlay mode so viewers can watch
   * the stream at full size while chat floats above.
   */
  public isLiveEvent: boolean = false;

  /**
   * The livestream event that hasn't started yet. While set, the player renders the shared
   * pre-show countdown (`fui-livestream-countdown`) in place of the stream — opening a livestream
   * early used to drop the viewer on the platform's own bare "starts in X" screen. Comments stay
   * visible alongside it (the fender.com embed is the one place that runs countdown-only).
   * Cleared when the countdown reports the event live, at which point the real player mounts.
   */
  public pendingLiveEvent: any = null;

  /**
   * "Show comments" pill collapses to icon-only after this delay (ms) so it stops competing with
   * the video for attention. Restored to full label whenever the panel is re-opened and hidden
   * again.
   */
  private readonly SHOW_COMMENTS_PILL_COLLAPSE_MS = 4000;
  public showCommentsPillCollapsed: boolean = false;
  private _showCommentsPillCollapseTimer: any = null;

  /**
   * FIFO stack of incoming comments shown as toasts when the panel is hidden — lets viewers stay
   * tuned to a livestream chat without keeping the panel open. Each toast has its own hold/fade
   * timer so quiet moments naturally empty the stack; a burst caps at HIDDEN_TOAST_MAX with the
   * oldest fading first. The stack is a plain array so *ngFor / @for renders each toast with its
   * own `visible` flag driving the CSS slide-in transition.
   */
  public hiddenIncomingToasts: Array<{ id: number; comment: any; visible: boolean; exiting: boolean; hideTimer: any; clearTimer: any }> = [];
  private _hiddenToastSeq: number = 0;
  private readonly HIDDEN_TOAST_MAX = 3;
  private readonly HIDDEN_TOAST_HOLD_MS = 8000;
  private readonly HIDDEN_TOAST_FADE_MS = 300;

  public videoRef: AngularFirestoreCollection;
  public videoCommentsRef: AngularFirestoreCollection;
  public videoConversationID: string;
  public videoCommentsSubscription: any;
  public loadingComments: boolean = true;

  public landscape: boolean = false;
  public pinnedToBottom: boolean = false;
  public shouldShowPinOption = true;

  /**
   * Overlay-mode height cap for the comments panel. YouTube's persistent chrome (top title bar +
   * bottom progress bar) leaves the middle of the iframe safe for a full-height overlay, so we
   * leave this null there. Vimeo draws its transport controls directly over the video content,
   * so we cap the overlay to the visible 16:9 video area minus a clearance for those controls.
   */
  public overlayMaxHeight: number | null = null;
  public comments: Array<any>;
  public commentDeleteIndex: number;

  public dockRight: boolean = true;

  // Resizable sidebar
  public sidebarWidth: number = 420;
  public resizing = false;
  private _resizeStartX: number = 0;
  private _sidebarWidthAtDragStart: number = 420;
  private _boundOnMouseMove = this._onResizeMouseMove.bind(this);
  private _boundOnMouseUp = this._onResizeMouseUp.bind(this);

  // Resizable overlay panel — the docked sidebar cares only about width; the overlay is a
  // free-floating box anchored top-right, so both dimensions are viewer-adjustable. Bottom-left
  // corner drives both at once: dragging left grows width, dragging down grows height. Values
  // null mean "use CSS default" — width 340 / height auto-bound by CSS top+bottom rule.
  public overlayWidth: number | null = null;
  public overlayHeight: number | null = null;
  public overlayResizing = false;
  private _overlayResizeStartX = 0;
  private _overlayResizeStartY = 0;
  private _overlayWidthAtDragStart = 0;
  private _overlayHeightAtDragStart = 0;
  private _boundOnOverlayResizeMove = this._onOverlayResizeMove.bind(this);
  private _boundOnOverlayResizeUp = this._onOverlayResizeUp.bind(this);
  private static readonly OVERLAY_MIN_WIDTH = 260;
  private static readonly OVERLAY_MIN_HEIGHT = 160;
  private static readonly OVERLAY_DEFAULT_WIDTH = 340;
  /** Matches the overlay panel's CSS `top: 60px`. */
  private static readonly OVERLAY_TOP = 60;
  /** Breathing room below the panel so the composer isn't flush against the window edge. */
  private static readonly OVERLAY_BOTTOM_GUTTER = 60;
  /**
   * Room left below the panel for the player's own transport controls, which both platforms draw
   * over the bottom of the video content (not over the letterbox bars).
   */
  private static readonly PLAYER_CONTROLS_CLEARANCE = 60;
  /** Floor for the render cap — roughly a composer plus one message. */
  private static readonly OVERLAY_CAP_FLOOR = 120;

  public videoStats: any;

  public vimeoProgress: any;
  public ytApiLoaded: boolean = false;

  public selectedSection: string = "comments";

  public ytCurrentTimeInterval: any;
  public updateProgressInterval: any;
  public shouldAutoPlay: boolean = false;
  public shouldShowVideo: boolean = true;

  public lesson: Lesson;
  public lessonWatchingIndex;
  public lessonNextVideo;
  public lessonPrevVideo;

  public parentThreadMap: any = {};
  public processedCommentMap: any = {};

  private _userJustSentComment: boolean = false;
  /** Index within the in-player video history stack (0 = first opened video). */
  private _videoNavIndex: number = -1;
  private _restoringFromHistory: boolean = false;
  private _closingPlayer: boolean = false;

  public activeTime: {
    display: string;
    seconds: number;
  };

  public presonusVideo: Video;
  public presonusVideoDescriptionHtml: string;
  public env = environment;
  public videoPlayerTabs: Array<any> = [];

  public playerVars: YT.PlayerVars = {};

  public selectedSectionWord: string = "Comments";

  @ViewChild("videoOverlay", { read: ElementRef }) videoOverlay: ElementRef;
  @ViewChild("closeButton", { static: false }) closeModal: ElementRef;
  @HostListener("document:keyup.escape", ["$event"]) onKeydownHandler(event: Event) {
    if (!this.embedMode && this.video) this.close();
  }
  @HostListener("window:resize", ["$event"])
  getScreenSize(event?) {
    this.measureUI();
  }

  @ViewChild("ytPlayer", { static: false }) ytPlayer: any;
  @ViewChild("chatCmp", { static: false }) chatCmp: ChatComponent;

  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.embedMode && event.key == "Escape") this.close();
  }

  @HostListener("window:popstate", ["$event"])
  onPopState(event: PopStateEvent) {
    if (this.embedMode || this.minimized || this._closingPlayer) return;

    const state = event?.state;
    // Back/forward within the in-player stack: restore that video instead of closing.
    if (state?.modal && state?.videoPlayer && this.video) {
      this._restoringFromHistory = true;
      this._videoNavIndex = typeof state.videoNavIndex === "number" ? state.videoNavIndex : 0;
      const payload = state.videoPlayer;
      this.videoReady = false;
      this.video = payload;
      this.videoReady = true;
      this.minimized = false;
      this.document.body.classList.add("video-open");
      this.videoInitted.next(payload);
      return;
    }

    if (this.video) {
      this.close({ fromPopState: true });
    }
  }

  constructor(
    storageService: StorageService,
    userService: UserService,
    private _win: WindowService,
    public eventBusService: EventBusService,
    public db: AngularFirestore,
    public fbAuth: AngularFireAuth,
    public appService: AppService,
    public helperService: HelperService,
    public contentService: ContentService,
    public communityService: WebCommunityService,
    public eventsService: EventsService,
    public activatedRoute: ActivatedRoute,
    public betaService: BetaService,
    public router: Router,
    private _location: Location,
    public productService: ProductService,
    public fbService: FirebaseService,
    private translate: TranslateService,
    private avoService: AvoService,
    private embedBridge: EmbedBridgeService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super(storageService, userService, eventBusService);
    this.updateVideoPlayerTabs();
  }

  private updateVideoPlayerTabs() {
    const isGuest = !this.user;
    this.videoPlayerTabs = [
      {
        name: "Details",
        translationKey: "video_player.details_tab",
        id: "details",
      },
      {
        name: "Comments",
        translationKey: "video_player.comments_tab",
        id: "comments",
        selected: true,
      },
    ];
    if (!isGuest) {
      this.videoPlayerTabs.push({
        name: "My Notes",
        translationKey: "video_player.my_notes_tab",
        id: "mynotes",
      });
    }
  }

  ngOnInit() {
    super.ngOnInit();
    // Embed defaults to overlaying comments on the video (viewer can toggle to the docked sidebar).
    if (this.embedMode) {
      // DENG-893: the panel loads minimized (a floating trigger restores it); its docked-vs-overlay
      // position is remembered, defaulting to docked on first open (see _defaultCommentsOverlay).
      this.commentsHidden = true;
      this.commentsOverlay = this._defaultCommentsOverlay();
      this._updatePanelFeatures();
      // Shopping rail: the host page posts product data over postMessage (see EmbedBridgeService).
      // Once products arrive the Shop tab appears and leads (auto-selected under the collapsed
      // panel); the viewer opens the panel to whichever tab is active.
      this.embedBridge.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
        this.shopContent = shop && shop.products.length ? shop : null;
        this.hasShop = !!this.shopContent;
        this._updatePanelFeatures();
        if (this.hasShop && !this._shopAutoOpened) {
          this._shopAutoOpened = true;
          this.selectSection("shop");
        } else if (!this.hasShop && this.selectedSection === "shop") {
          this.selectSection(this.panelFeatures[0]?.id || "comments");
        }
        this.measureUI();
      });
    }
    this.loadSidebarWidth();
    this.videoInitted.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      const wasAlreadyOpen = !this.embedMode && this.document.body.classList.contains("video-open");

      // Clean up timers from the previous video when switching in-place.
      if (this.ytCurrentTimeInterval) this._win.clearInterval(this.ytCurrentTimeInterval);
      if (this.updateProgressInterval) this._win.clearInterval(this.updateProgressInterval);

      this.shouldShowVideo = true;
      const livestreamEvent = this._resolveLivestreamEvent(result);
      const timing = livestreamEvent ? this.eventsService.getEventTimingState(livestreamEvent) : null;
      this.isLiveEvent = result?.isLiveEvent === true || timing === "live";
      // Opened before showtime: hold on the countdown instead of the stream. Embed mode runs its
      // own countdown around this component, so it never lands here.
      this.pendingLiveEvent = !this.embedMode && !this.isLiveEvent && timing === "upcoming" ? livestreamEvent : null;
      // MyFender (non-embed) livestream: surface the same panel the embed does — respect the event's
      // comments_enabled and expose the Sign-up CTA. Shop is intentionally left out (its rail arrives
      // via postMessage from the fender.com host, which doesn't exist in the in-app player).
      // "Watch Trailer" (from the event page) opened the player — the countdown opens its trailer modal.
      this.autoOpenTrailer = !!result?.openTrailer;
      const isLivestreamInApp = !this.embedMode && !!livestreamEvent;
      if (isLivestreamInApp) {
        this.commentsEnabled = livestreamEvent.comments_enabled !== false;
        this.signupEnabled = livestreamEvent.signup_enabled === true;
        // Live panel uses the "livestream" state; the pre-show countdown uses the "countdown" state.
        this.signupConfig = livestreamEvent.signup_config?.livestream || null;
        this.signupConfigCountdown = livestreamEvent.signup_config?.countdown || null;
        this.signupEventId = livestreamEvent.id;
      }
      // The comments/sign-up side panel belongs to the LIVE stream. During the pre-show countdown
      // (pendingLiveEvent) the countdown screen owns sign-up via its own button + modal, so the panel
      // stays hidden; onLivestreamWentLive() turns it on when the stream starts.
      this.hasLivestreamPanel = isLivestreamInApp && !this.pendingLiveEvent;
      if (isLivestreamInApp && this.pendingLiveEvent) {
        // Countdown: keep the panel collapsed. The floating "Show Comments" pill (shown when comments
        // are enabled) lets the viewer open it; sign-up stays on the countdown's own button.
        this.commentsHidden = true;
      }
      // Overlay is available for every video (see toggleCommentsOverlay); it's only the DEFAULT
      // for a stream or its countdown, where the panel would otherwise squeeze a feed the viewer
      // is watching. A viewer who has picked a side keeps it — including across the countdown
      // handing off to the live stream.
      this.commentsOverlay = this._defaultCommentsOverlay();
      this.lesson = result.lesson;
      // Always re-resolve catalog metadata when switching videos in-place.
      this.presonusVideo = this.video.presonusVideo || (this.video.presonus_id ? this.contentService.videosById[this.video.presonus_id] : undefined);
      this.presonusVideoDescriptionHtml = this.presonusVideo?.description ? this.helperService.makeHtml(this.presonusVideo.description) : "";
      if (this.lesson) {
        this.lessonWatchingIndex = 0;
        this.lesson.videos.forEach((item, index) => {
          if (item.id == this.video.presonus_id) this.lessonWatchingIndex = index;
        });
        this.lessonNextVideo = this.lesson.videos[this.lessonWatchingIndex + 1] ? this.lesson.videos[this.lessonWatchingIndex + 1] : false;
        this.lessonPrevVideo = this.lesson.videos[this.lessonWatchingIndex - 1] ? this.lesson.videos[this.lessonWatchingIndex - 1] : false;
      }

      // Embed: land on the lead tab — Shop when the host has sent products ("Shop This Livestream"
      // leads), otherwise the first enabled feature. videoInitted and the shop postMessage handshake
      // race, so rebuild the feature set here too and don't clobber an already-chosen shop tab.
      if (this.embedMode || this.hasLivestreamPanel) {
        this._updatePanelFeatures();
        const lead = this.hasShop && this.shopEnabled ? "shop" : this.panelFeatures[0]?.id || "comments";
        this.selectSection(lead);
      } else {
        this.selectedSection = "comments";
        this.selectedSectionWord = this.translate.instant("video_player.comments_tab");
      }
      if (!this.embedMode) this.document.body.classList.add("video-open");
      this.updateVideoPlayerTabs();
      // Fire Loaded a Page for Video page (only in My Fender)
      if (this.userService.appId === "fender") {
        this.avoService.avoInitted$
          .pipe(
            filter((v) => !!v),
            take(1),
            takeUntil(this.destroy$)
          )
          .subscribe(() => {
            const te = this.avoService.trackEvent();
            if (te && typeof te.loadedAPage === "function") {
              const pageName = this.video?.title || "Video Page";
              const page = this.avoService.getPageProperties({ pageName });
              const payload: any = {
                ...page,
                customDestinationPageName_: pageName,
              };
              te.loadedAPage(payload);
            }
          });
      }
      if (this.canShowVideoComments()) {
        this.setupComments({ switchingInPlace: wasAlreadyOpen });
      } else {
        this.loadingComments = false;
      }
      this.measureUI();

      this.shouldAutoPlay = true;
      if (this.presonusVideo && this.presonusVideo.subscription_only && !this.user?.active_subscription) {
        this.shouldAutoPlay = false;
      }
      this.playerVars = {
        modestbranding: 1,
        rel: 0,
        autoplay: this.shouldAutoPlay ? 1 : 0,
        // Muted autoplay for embeds (browsers block unmuted cross-origin autoplay).
        mute: this.video?.muted ? 1 : 0,
        // Start offset in seconds (embed ?t=), applied to YouTube playback.
        start: this.video?.start ? Math.floor(this.video.start) : 0,
      };

      if (this.presonusVideo) {
        if (this.presonusVideo.subscription_only && !this.user?.active_subscription) {
          this.shouldShowVideo = false;
        }
      }
      // In embed mode, don't rewrite the URL — it would clobber the embed's own query params
      // (videoId/hideComments) and break refresh.
      if (!this.embedMode) {
        this.syncVideoHistory(wasAlreadyOpen);
      }
      // While the countdown is up there's no iframe to attach to yet — onLivestreamWentLive()
      // runs this once the stream actually mounts.
      if (!this.pendingLiveEvent) this.initPlayerPlayback();
    });

    // In embed mode we don't wait for the global playVideo event bus — start the given
    // video directly so the player renders inline inside the iframe.
    if (this.embedMode && this.initialVideo && !this.video) {
      this.video = this.initialVideo;
      this.videoReady = true;
      this.videoInitted.next(this.initialVideo);
    }
  }

  /**
   * The pre-show countdown reached the event start time — swap the countdown out for the real
   * stream and boot playback. `commentsOverlay` is deliberately left alone: the countdown already
   * defaulted it on, so re-forcing it here would undo a viewer who docked the panel mid-countdown.
   */
  onLivestreamWentLive() {
    this.pendingLiveEvent = null;
    this.isLiveEvent = true;
    // Countdown handed off to the stream — reveal the live comments/sign-up panel (MyFender). The
    // embed runs its own countdown and never calls this, so this path is in-app only.
    if (!this.embedMode) {
      this.hasLivestreamPanel = true;
      this.commentsHidden = false;
      this._updatePanelFeatures();
      const lead = this.hasShop && this.shopEnabled ? "shop" : this.panelFeatures[0]?.id || "comments";
      this.selectSection(lead);
    }
    this.measureUI();
    this.initPlayerPlayback();
  }

  /**
   * If an unmuted autoplay attempt didn't start (browser blocked it without a user gesture), mute
   * and retry so the video still plays. The YouTube player state is checked after a short delay:
   * 1 = playing, 3 = buffering — anything else means it never started.
   */
  private ensureAutoplayFallback() {
    this._win.setTimeout(() => {
      const player = this.ytPlayer?._player;
      if (!player || typeof player.getPlayerState !== "function") return;
      const state = player.getPlayerState();
      if (state !== 1 && state !== 3) {
        if (player.mute) player.mute();
        if (player.playVideo) player.playVideo();
      }
    }, 1200);
  }

  /**
   * Attach to the platform player and start progress tracking. Deferred behind timeouts because
   * both platforms need their iframe in the DOM first (and YouTube its API script loaded).
   */
  private initPlayerPlayback() {
    if (this.video.type == "vimeo" && this.shouldShowVideo) {
      this.initVimeoProgressTracker();
    } else {
      if (!this.ytApiLoaded) {
        // This code loads the IFrame Player API code asynchronously, according to the instructions at
        // https://developers.google.com/youtube/iframe_api_reference#Getting_Started
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
        this.ytApiLoaded = true;
      }
      if (this.shouldShowVideo) {
        this._win.setTimeout(() => {
          if (this.ytPlayer) {
            this.videoStats = this.communityService.userVideoDataMap[this.video.type + "_" + this.video.id];
            if (this.shouldAutoPlay && this.ytPlayer?._player?.playVideo) {
              this.ytPlayer._player.playVideo();
              // The countdown→live handoff (and the chrome-less embed) start playback on a timer, with
              // no user gesture — so the browser blocks UNMUTED autoplay. Try with sound first; if it
              // hasn't started shortly after, mute and retry so the stream/recording at least plays
              // (the viewer can unmute via the player controls). Scoped to the live/embed autostart so
              // it never mutes an ordinary in-app video the viewer pressed play on.
              if (this.isLiveEvent || this.embedMode) this.ensureAutoplayFallback();
            }
            this.autoUpdateProgress();
            // Live broadcasts have no "watched position" to resume from — seeking would jump
            // the viewer backward from the live edge (or fail entirely). Only replay saved
            // positions for recorded videos.
            if (
              !this.isLiveEvent &&
              this.contentService.userLearnData.videos[this.video.presonus_id] &&
              this.contentService.userLearnData.videos[this.video.presonus_id].watched &&
              this.ytPlayer?._player?.seekTo
            ) {
              this.ytPlayer._player.seekTo(this.contentService.userLearnData.videos[this.video.presonus_id].watched);
            }
            this.ytCurrentTimeInterval = this._win.setInterval(() => {
              if (this.ytPlayer && this.ytPlayer._player) {
                this.activeTime = {
                  display: this.helperService.hhmmss(parseInt(this.ytPlayer._player.getCurrentTime().toFixed(0))),
                  seconds: parseInt(this.ytPlayer._player.getCurrentTime().toFixed(0)),
                };
              } else {
                this.activeTime = undefined;
              }
            }, 1000);
          }
        }, 2000);
      }
    }
  }

  selectSection(id) {
    this.selectedSection = id;
    // Feature-panel tabs (comments/shop/sign-up) carry their own labels — used by the embed and the
    // in-app livestream player; the ordinary in-app player's tabs (details/comments/mynotes) come
    // from the translated tab list.
    if (this.embedMode || this.hasLivestreamPanel) {
      const feat = this.panelFeatures.find((f) => f.id === id);
      if (feat) {
        this.selectedSectionWord = feat.label;
        return;
      }
    }
    let translateKey = this.videoPlayerTabs.find((tab) => tab.id === id)?.translationKey || "video_player.comments_tab";
    this.selectedSectionWord = this.translate.instant(translateKey);
  }

  /**
   * Rebuild the enabled embed panel features (DENG-893), in tab order: Comments, Shop, Sign-up.
   * Only features that are on — and, for Shop, actually have product data — produce a tab. Also
   * refreshes the minimized-trigger icon/label and keeps `selectedSection` pointed at a live tab.
   */
  private _updatePanelFeatures() {
    if (!this.embedMode && !this.hasLivestreamPanel) return;
    const features: Array<{ id: string; label: string; icon: string }> = [];
    if (this.commentsEnabled) features.push({ id: "comments", label: this.translate.instant("video_player.comments_tab"), icon: "comment" });
    if (this.hasShop && this.shopEnabled) features.push({ id: "shop", label: "Shop", icon: "cart-shopping" });
    if (this.signupEnabled) features.push({ id: "signup", label: this.signupConfig?.button_text || "Sign Up", icon: "envelope" });
    this.panelFeatures = features;
    this.hasPanelContent = features.length > 0;
    this._updatePanelTrigger();
    // Keep the active tab valid as features come and go (e.g. shop arriving/leaving).
    if (features.length && !features.find((f) => f.id === this.selectedSection)) {
      this.selectSection(features[0].id);
    }
  }

  /**
   * Minimized-trigger (floating pill) icon + label — names what the panel actually opens (DENG-893).
   * Shop shows the cart; comments+shop reads "Show Comments & Shop". Sign-up is a secondary CTA, so
   * it's only named when it's the sole feature (otherwise it rides along under comments/shop).
   */
  private _updatePanelTrigger() {
    const has = (id: string) => this.panelFeatures.some((f) => f.id === id);
    const comments = has("comments");
    const shop = has("shop");
    const signup = has("signup");

    // Icon: shop's cart is the eye-catch when present; else comments; else sign-up.
    this.panelTriggerIcon = shop ? "cart-shopping" : comments ? "comment" : signup ? "envelope" : "comment";

    if (comments && shop) {
      this.panelTriggerLabel = "Show Comments & Shop";
    } else if (shop) {
      this.panelTriggerLabel = "Shop";
    } else if (comments) {
      this.panelTriggerLabel = "Show comments";
    } else if (signup) {
      this.panelTriggerLabel = this.signupConfig?.button_text || "Sign up";
    } else {
      this.panelTriggerLabel = "Show comments";
    }
  }

  focusOnCloseButton() {
    if (this.closeModal?.nativeElement) this.closeModal.nativeElement.focus();
  }

  get dialogAriaLabel(): string {
    const title = this.video?.title ?? "";
    return this.translate.instant("video.player_dialog_label", { title });
  }

  public vimeoPlayer: Player;
  initVimeoProgressTracker() {
    this.videoStats = this.communityService.userVideoDataMap[this.video.type + "_" + this.video.id];
    this._win.setTimeout(() => {
      let frame = document.getElementById("vimeo_frame");
      if (!frame) return;
      this.vimeoPlayer = new Player(frame);
      this.vimeoPlayer.on("timeupdate", (data) => {
        this.vimeoProgress = data;
        this.activeTime = {
          display: this.helperService.hhmmss(parseInt(this.vimeoProgress.seconds)),
          seconds: parseInt(this.vimeoProgress.seconds),
        };
      });
      this.vimeoPlayer.on("play", (data) => {
        this.autoUpdateProgress();
      });
      this.vimeoPlayer.on("pause", (data) => {
        if (this.updateProgressInterval) this._win.clearInterval(this.updateProgressInterval);
        this.updateProgress();
      });
      this.vimeoPlayer.play();
      // Skip the saved-position seek for livestreams — there's no meaningful watched offset for
      // a live broadcast (Vimeo would just clamp to the current live edge, but the intent
      // wasn't to resume anyway).
      if (!this.isLiveEvent && this.contentService.userLearnData.videos[this.video.presonus_id] && this.contentService.userLearnData.videos[this.video.presonus_id].watched) {
        this.vimeoPlayer.setCurrentTime(this.contentService.userLearnData.videos[this.video.presonus_id].watched);
      }
    }, 1000);
  }

  showPreviousVideo() {
    let args: any = {
      id: this.lessonPrevVideo.platform_id,
      title: this.lessonPrevVideo.title,
      type: this.lessonPrevVideo.platform,
      thumb: this.lessonPrevVideo.image,
      presonus_id: this.lessonPrevVideo.id,
    };
    args.lesson = this.contentService.lessonMap[this.lesson.id];
    this.eventBusService.emit(this.eventBusService.types.playVideo, args);
  }

  goToLesson() {
    this.close();
    this.router.navigate(["/learn/lesson", this.lesson.id]);
  }

  goToProduct(product) {
    if (this.productService.productsByProductID[product.id]) {
      this.minimizeVideoPlayer();
      this.router.navigate(["/products/detail", product.id]);
    }
  }

  upgrade() {
    this.close();
    this.router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  seekTo(seconds) {
    if (this.presonusVideo.platform == "youtube" && this.ytPlayer && this.ytPlayer._player?.seekTo) {
      this.ytPlayer._player.seekTo(seconds);
    } else {
      this.vimeoPlayer.setCurrentTime(seconds);
      this.vimeoPlayer.play();
    }
  }

  autoUpdateProgress() {
    if (this.updateProgressInterval) this._win.clearInterval(this.updateProgressInterval);
    this.updateProgressInterval = this._win.setInterval(() => {
      this.updateProgress(true);
    }, 5000);
  }

  updateProgress(onlyIfPlaying = false) {
    if (!this.user) return;
    if (this.video && this.video.type == "vimeo" && this.vimeoProgress) {
      if (this.video.presonus_id) {
        this.contentService
          .saveUserVideoData({
            user_id: this.user.id,
            video_id: this.video.presonus_id,
            watched: this.vimeoProgress.seconds,
            last_watched: new Date(),
          })
          .subscribe({
            next: (result) => {},
            error: (error) => {
              this.appService.alertError(error);
            },
          });
      }
    } else if (this.video && this.video.type == "youtube" && this.ytPlayer && this.ytPlayer._player) {
      if (!onlyIfPlaying || (onlyIfPlaying && this.ytPlayer._player.getPlayerState() == 1)) {
        let args = {
          seconds: this.ytPlayer._player.getCurrentTime(),
          duration: this.ytPlayer._player.getDuration(),
          percent: (this.ytPlayer._player.getCurrentTime() * 100) / this.ytPlayer._player.getDuration(),
        };
        if (args.seconds) {
          if (this.video.presonus_id) {
            this.contentService
              .saveUserVideoData({
                user_id: this.user.id,
                video_id: this.video.presonus_id,
                watched: this.ytPlayer._player.getCurrentTime(),
                last_watched: new Date(),
              })
              .subscribe({
                next: (result) => {},
                error: (error) => {
                  this.appService.alertError(error);
                },
              });
          }
        }
      }
    }
  }

  measureUI() {
    // `landscape` must track the SAME breakpoint the CSS uses to switch between the
    // side-by-side (row) and stacked (column) layouts — see _video.scss:
    //   row:    (min-aspect-ratio: 1/1) and (min-width: 768px)
    //   column: (max-aspect-ratio: 1/1), (max-width: 767px)
    // Deriving it from a different rule (e.g. innerHeight < innerWidth - N) let the two
    // disagree, which pinned the fixed sidebar width onto the stacked (bottom) panel until
    // the window shrank far enough for the old rule to flip. matchMedia keeps them in sync.
    // DENG-893: the 768px gate (down from 950) lets landscape phones side-dock instead of
    // stacking into a cramped strip under the video — the aspect gate still excludes portrait.
    const sideBySide = window.matchMedia("(min-aspect-ratio: 1/1) and (min-width: 768px)").matches;
    this.landscape = sideBySide;
    // Pinning comments to the bottom is only meaningful in the side-by-side layout; once the
    // layout stacks, comments already sit at the bottom, so hide the toggle.
    this.shouldShowPinOption = sideBySide;
    this.overlayMaxHeight = this._computeOverlayMaxHeight();
  }

  /**
   * The livestream event behind this video, if any: whatever the caller attached (`event`),
   * otherwise a lookup against the cached public events by platform video id. Callers that
   * already have the event should pass it — the cache can be empty on a fresh page load, and
   * without the event we can't tell "not a livestream" from "hasn't started yet". Timing is
   * always re-derived from the event's start/end rather than a cached `in_progress` flag, which
   * may have been computed hours ago on a long-lived tab.
   */
  private _resolveLivestreamEvent(video: any): any {
    if (!video) return null;
    if (video.event) return video.event;
    return this.eventsService.findLivestreamEventByVideoId(String(video.id));
  }

  /**
   * Height cap for the floating comments panel, applied as a `max-height` on every render.
   *
   * Two things bound it, and the tighter one wins:
   *  1. The window. The panel is anchored `top: 60px` and absolutely positioned inside a fixed,
   *     non-scrolling shell, and the message composer sits at its bottom edge — so any height past
   *     the fold makes the composer unreachable, not merely clipped. A height remembered from a
   *     tall window must never survive into a short one. Capping at render (rather than rewriting
   *     the stored value) keeps the viewer's preference and hands it back when there's room again.
   *  2. The video content. Both platforms letterbox 16:9 content inside the container and draw
   *     their transport controls over the bottom of that content, so the panel stops above it
   *     rather than hanging over (or covering the controls of) the media it floats on.
   *
   * Returns null when nothing is floating — the docked sidebar takes its height from the flex row.
   */
  private _computeOverlayMaxHeight(): number | null {
    if (!this.commentsOverlay || !this.landscape) return null;

    const containerW = window.innerWidth;
    const containerH = window.innerHeight;
    let cap = containerH - VideoPlayerComponent.OVERLAY_TOP - VideoPlayerComponent.OVERLAY_BOTTOM_GUTTER;

    // The countdown fills its container edge to edge — no letterbox and no transport controls.
    if (!this.pendingLiveEvent) {
      const videoDisplayH = Math.min(containerH, (containerW * 9) / 16);
      const videoContentBottom = containerH / 2 + videoDisplayH / 2;
      cap = Math.min(cap, videoContentBottom - VideoPlayerComponent.PLAYER_CONTROLS_CLEARANCE - VideoPlayerComponent.OVERLAY_TOP);
    }

    return Math.max(VideoPlayerComponent.OVERLAY_CAP_FLOOR, cap);
  }

  onResizeMouseDown(event: MouseEvent) {
    if (event.button !== 0) return; // left-click only
    event.preventDefault();
    this.resizing = true;
    this._resizeStartX = event.clientX;
    this._sidebarWidthAtDragStart = this.sidebarWidth;
    document.addEventListener("mousemove", this._boundOnMouseMove);
    document.addEventListener("mouseup", this._boundOnMouseUp);
  }

  private _onResizeMouseMove(event: MouseEvent) {
    if (!this.resizing) return;
    const delta = this._resizeStartX - event.clientX;
    this.sidebarWidth = Math.max(300, Math.min(this._sidebarWidthAtDragStart + delta, window.innerWidth * 0.5));
  }

  private _onResizeMouseUp() {
    this.resizing = false;
    document.removeEventListener("mousemove", this._boundOnMouseMove);
    document.removeEventListener("mouseup", this._boundOnMouseUp);
    try {
      localStorage.setItem("videoPlayerSidebarWidth", String(this.sidebarWidth));
    } catch (e) {}
  }

  private loadSidebarWidth() {
    try {
      const saved = localStorage.getItem("videoPlayerSidebarWidth");
      if (saved) this.sidebarWidth = Math.max(300, Math.min(parseInt(saved, 10), window.innerWidth * 0.5));
    } catch (e) {}
    try {
      const w = localStorage.getItem("videoPlayerOverlayWidth");
      const h = localStorage.getItem("videoPlayerOverlayHeight");
      // Stored as-is, NOT clamped to the current window: the preference is the viewer's, and
      // `overlayMaxHeight` caps what actually renders. Widen the window again and they get their
      // full height back.
      if (w) this.overlayWidth = parseInt(w, 10);
      if (h) this.overlayHeight = parseInt(h, 10);
      const overlay = localStorage.getItem("videoPlayerCommentsOverlay");
      if (overlay === "1" || overlay === "0") this._storedCommentsOverlay = overlay === "1";
    } catch (e) {}
  }

  /**
   * Bottom-left corner drag for the overlay panel. Panel is anchored top: 60px, right: 20px in
   * the CSS, so we grow toward the left (delta X flips sign vs. the docked sidebar handle) and
   * downward. Constraints:
   *  - min OVERLAY_MIN_WIDTH x OVERLAY_MIN_HEIGHT
   *  - max width capped so we can't cover the entire viewport
   *  - max height capped by `overlayMaxHeight` when set (vimeo controls clearance)
   */
  onOverlayResizeMouseDown(event: MouseEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.overlayResizing = true;
    this._overlayResizeStartX = event.clientX;
    this._overlayResizeStartY = event.clientY;
    // If width/height weren't set yet, capture the actual rendered size so drag deltas apply
    // from the CSS defaults instead of from 0.
    const container = (event.currentTarget as HTMLElement).closest(".comments-container") as HTMLElement | null;
    const rect = container?.getBoundingClientRect();
    // Prefer the measured size over the stored one: what's on screen may be shorter than the
    // remembered height (capped to fit the window), and dragging has to start from what the
    // viewer can see or the panel jumps under the cursor.
    this._overlayWidthAtDragStart = rect?.width ?? this.overlayWidth ?? VideoPlayerComponent.OVERLAY_DEFAULT_WIDTH;
    this._overlayHeightAtDragStart = rect?.height ?? this.overlayHeight ?? VideoPlayerComponent.OVERLAY_MIN_HEIGHT;
    document.addEventListener("mousemove", this._boundOnOverlayResizeMove);
    document.addEventListener("mouseup", this._boundOnOverlayResizeUp);
  }

  private _onOverlayResizeMove(event: MouseEvent) {
    if (!this.overlayResizing) return;
    // Bottom-left corner: mouse moves LEFT → width increases, mouse moves DOWN → height increases.
    const deltaX = this._overlayResizeStartX - event.clientX;
    const deltaY = event.clientY - this._overlayResizeStartY;
    const maxW = Math.max(VideoPlayerComponent.OVERLAY_MIN_WIDTH, window.innerWidth - 80);
    // Same cap the render uses, so you can't drag the composer off the bottom of the window.
    const maxH = this.overlayMaxHeight ?? this._computeOverlayMaxHeight() ?? window.innerHeight - VideoPlayerComponent.OVERLAY_TOP - VideoPlayerComponent.OVERLAY_BOTTOM_GUTTER;
    // In a very short window the cap can fall below the normal minimum; the cap wins.
    const minH = Math.min(VideoPlayerComponent.OVERLAY_MIN_HEIGHT, maxH);
    this.overlayWidth = Math.max(VideoPlayerComponent.OVERLAY_MIN_WIDTH, Math.min(this._overlayWidthAtDragStart + deltaX, maxW));
    this.overlayHeight = Math.max(minH, Math.min(this._overlayHeightAtDragStart + deltaY, maxH));
  }

  private _onOverlayResizeUp() {
    this.overlayResizing = false;
    document.removeEventListener("mousemove", this._boundOnOverlayResizeMove);
    document.removeEventListener("mouseup", this._boundOnOverlayResizeUp);
    try {
      if (this.overlayWidth != null) localStorage.setItem("videoPlayerOverlayWidth", String(this.overlayWidth));
      if (this.overlayHeight != null) localStorage.setItem("videoPlayerOverlayHeight", String(this.overlayHeight));
    } catch (e) {}
  }

  /**
   * Keep browser history in sync with in-player video navigation so Back returns to the
   * previous video (with its comments) instead of leaving the player entirely.
   */
  private syncVideoHistory(wasAlreadyOpen: boolean) {
    if (this.embedMode || !this.video) return;

    // Browser already updated URL/state when restoring via Back/Forward.
    if (this._restoringFromHistory) {
      this._restoringFromHistory = false;
      return;
    }

    this._videoNavIndex = wasAlreadyOpen ? this._videoNavIndex + 1 : 0;

    const state: any = {
      modal: true,
      videoNavIndex: this._videoNavIndex,
      videoPlayer: {
        id: this.video.id,
        title: this.video.title,
        type: this.video.type,
        thumb: this.video.thumb,
        presonus_id: this.video.presonus_id,
      },
    };

    let url = location.pathname + (location.search || "");
    if (this.video.presonus_id) {
      const params = new HttpParams().appendAll({
        viewingVideo: String(this.video.presonus_id),
      });
      url = `${location.pathname}?${params.toString()}`;
      state.viewingVideo = this.video.presonus_id;
    }

    history.pushState(state, "", url);
  }

  private clearVideoCommentsState(setLoading = true) {
    if (this.videoCommentsSubscription?.unsubscribe) {
      this.videoCommentsSubscription.unsubscribe();
      this.videoCommentsSubscription = undefined;
    }
    this.communityService.comments = [];
    this.communityService.replies = [];
    this.communityService.parentThreadMap = {};
    this.communityService.processedCommentMap = {};
    // Avoid toggling loadingComments during in-place switches — that flag wraps the
    // entire player UI and would unmount the video while comments reload.
    if (setLoading) this.loadingComments = true;
  }

  setupComments(options?: { switchingInPlace?: boolean }) {
    // Reset prior thread state so in-place video switches don't keep old comments.
    this.clearVideoCommentsState(!options?.switchingInPlace);
    this.videoConversationID = this.video.type + "_" + this.video.id;
    this.videoRef = this.fbService.collection("video_comments");
    const videoDocRef = this.fbService.doc(`video_comments/${this.videoConversationID}`);

    videoDocRef.ref
      .get()
      .then((documentSnapshot) => {
        if (!documentSnapshot.exists) {
          if (!this.user) {
            // Guests can't create the parent doc; just wire up read-only comments.
            this.videoCommentsRef = this.fbService.collection(`video_comments/${this.videoConversationID}/comments`);
            this.initComments();
            return;
          }
          let video: any = {
            created: new Date(),
            video_id: this.video.id,
            type: this.video.type,
          };
          if (this.video.presonus_id) video.presonus_id = this.video.presonus_id;
          if (this.video.title) video.title = this.video.title;
          if (this.video.thumb) video.thumb = this.video.thumb;
          videoDocRef.set(video).then((doc) => {
            this.videoCommentsRef = this.fbService.collection(`video_comments/${this.videoConversationID}/comments`);
            this.initComments();
          });
        } else {
          this.videoCommentsRef = this.fbService.collection(`video_comments/${this.videoConversationID}/comments`);
          this.initComments();
        }
      })
      .catch(() => {
        // Firestore permission or auth failure — render player without comments.
        this.loadingComments = false;
      });
  }

  initComments() {
    let initialLoad = true;
    let previousCount = 0;
    if (this.videoCommentsSubscription?.unsubscribe) {
      this.videoCommentsSubscription.unsubscribe();
    }
    this.videoCommentsSubscription = this.communityService
      .subscribeToComments(this.videoCommentsRef, "asc")
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          const wasLoading = this.loadingComments;
          this.loadingComments = false;
          const currentCount = this.communityService.comments ? this.communityService.comments.length : 0;
          if (initialLoad) {
            initialLoad = false;
            previousCount = currentCount;
            if (wasLoading) {
              this._win.setTimeout(() => {
                if (this.chatCmp) this.chatCmp.scrollChatToBottom();
              }, 50);
            }
          } else if (currentCount > previousCount) {
            previousCount = currentCount;
            if (this._userJustSentComment) {
              this._userJustSentComment = false;
              this._win.setTimeout(() => {
                if (this.chatCmp) this.chatCmp.scrollChatToBottom();
              }, 50);
            } else {
              this._win.setTimeout(() => {
                const lastMsg = this.communityService.comments[this.communityService.comments.length - 1];
                if (this.commentsHidden) {
                  // Panel isn't rendered — surface the comment as a floating toast so viewers on a
                  // livestream (or anyone who collapsed the panel) still see incoming chat.
                  this._showHiddenIncomingComment(lastMsg);
                } else if (this.chatCmp) {
                  this.chatCmp.onNewMessageReceived(lastMsg);
                }
              }, 50);
            }
          } else {
            previousCount = currentCount;
          }
        },
        (error) => {
          this.loadingComments = false;
        }
      );
  }

  checkActiveReplyInputs(): boolean {
    const replyInputs = document.querySelectorAll<HTMLElement>('[id$="_reply_input"]');

    return Array.from(replyInputs).some((el) => (el.textContent?.trim() ?? "") !== "");
  }

  flagComment(msg) {
    this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${msg.key}`).update({
      flags: this.fbService.arrayUnion(this.userService.user.id),
    });
  }

  unflagComment(msg) {
    this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${msg.key}`).update({
      flags: this.fbService.arrayRemove(this.userService.user.id),
    });
  }

  likeComment(msg) {
    const userId = this.userService.user.id;
    let nextTypes: ReactionType[];
    if (Object.prototype.hasOwnProperty.call(msg, "nextReactions")) {
      nextTypes = Array.isArray(msg.nextReactions) ? msg.nextReactions : [];
    } else {
      const selected: ReactionType = msg.selectedReactionType || "like";
      const nextMap = applyReactionLocally(normalizeReactions(msg), userId, selected);
      nextTypes = nextMap[userId] || [];
    }
    const update: Record<string, any> = {
      [`reactions.${userId}`]: nextTypes.length ? nextTypes : this.fbService.deleteField(),
    };
    this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${msg.key}`).update(update);
  }

  /**
   * Video comments are gated on the `videoCommentsPublicAccess` flag AND presence of a user.
   * When the flag is off, guests get the sign-in CTA; authed users still see the real chat.
   * When the flag is on, everyone sees the chat (still read-only for guests since Firestore
   * rules and the chat component itself gate writes on auth).
   */
  canShowVideoComments(): boolean {
    // Respect the admin's comments toggle everywhere — the embed AND the in-app livestream player
    // (commentsEnabled is seeded from the event's comments_enabled for a livestream; it stays true
    // for ordinary Learn videos). When disabled, skip Firestore setup entirely.
    if (!this.commentsEnabled) return false;
    // Embed (fender.com iframe): comments render whenever the admin left them enabled for
    // this livestream; guests are read-only (enforced by the chat component + Firestore rules).
    if (this.embedMode) return this.commentsEnabled;
    if ((this.env?.features as any)?.videoCommentsPublicAccess) return true;
    return !!this.user;
  }

  /**
   * Whether the side panel should render right now. In embed the panel carries comments and/or
   * the shopping rail, so it shows when either has content (and the viewer hasn't collapsed it).
   */
  shouldShowCommentsPanel(): boolean {
    // Gated FSP+ videos show a subscribe splash instead of the video — hide comments too.
    if (!this.shouldShowVideo) return false;
    // Embed + in-app livestream share the multi-feature panel: show it whenever any feature (comments
    // or sign-up) has content and the viewer hasn't collapsed it.
    if (this.embedMode || this.hasLivestreamPanel) return this.hasPanelContent && !this.commentsHidden;
    return this.commentsEnabled && !this.commentsHidden;
  }

  /**
   * Whether the floating pill actions (show comments/shop, and close outside the embed) should
   * render: the panel has content but the viewer collapsed it. Skipped while minimized — that
   * layout hides the panel entirely and carries its own maximize/close bar.
   */
  shouldShowCommentsButton(): boolean {
    if (!this.shouldShowVideo || this.minimized) return false;
    if (this.embedMode || this.hasLivestreamPanel) return this.hasPanelContent && this.commentsHidden;
    return this.commentsEnabled && this.commentsHidden;
  }

  /** Swap the panel to the shopping rail. */
  showShop() {
    this.selectSection("shop");
  }

  /** Swap the panel back to comments. */
  showComments() {
    this.selectSection("comments");
  }

  /**
   * Overlay on or off for the current video: the viewer's remembered choice if they've made one,
   * otherwise on for a livestream (or its pre-show countdown) and the embed, off for a recording.
   */
  private _defaultCommentsOverlay(): boolean {
    if (this._storedCommentsOverlay !== null) return this._storedCommentsOverlay;
    // DENG-893: the embed opens docked to the side on first use (then remembers the viewer's pick).
    // In the in-app player a live stream / its countdown still defaults to overlay so chat floats
    // over full-size video.
    if (this.embedMode) return false;
    return this.isLiveEvent || !!this.pendingLiveEvent;
  }

  toggleCommentsOverlay() {
    this.commentsOverlay = !this.commentsOverlay;
    // An explicit pick sticks, the same way the panel's size does.
    this._storedCommentsOverlay = this.commentsOverlay;
    try {
      localStorage.setItem("videoPlayerCommentsOverlay", this.commentsOverlay ? "1" : "0");
    } catch (e) {}
    this.measureUI();
  }

  hideEmbedComments() {
    this.commentsHidden = true;
    // Pills enter fully expanded so the viewer reads them, then collapse to icon-only after a
    // few seconds. Reset any prior timer in case the panel was toggled hidden->shown->hidden.
    this.showCommentsPillCollapsed = false;
    if (this._showCommentsPillCollapseTimer) clearTimeout(this._showCommentsPillCollapseTimer);
    this._showCommentsPillCollapseTimer = setTimeout(() => {
      this.showCommentsPillCollapsed = true;
    }, this.SHOW_COMMENTS_PILL_COLLAPSE_MS);
    this.measureUI();
  }

  showEmbedComments() {
    this.commentsHidden = false;
    if (this._showCommentsPillCollapseTimer) clearTimeout(this._showCommentsPillCollapseTimer);
    this.showCommentsPillCollapsed = false;
    this._dismissHiddenIncomingComments();
    this.measureUI();
  }

  /**
   * Append an incoming comment to the hidden-toast stack. Each toast has its own hold/fade
   * timers so items age independently — no shared timer to reset on burst. When the stack is
   * full we drop the oldest immediately (its timers are cleared to avoid a stale fade later).
   * The `setTimeout` before flipping `visible = true` lets Angular render the new item in its
   * `translateY(100%)` initial state so the CSS transition to `translateY(0)` actually plays.
   */
  private _showHiddenIncomingComment(comment: any) {
    if (!comment) return;
    while (this.hiddenIncomingToasts.length >= this.HIDDEN_TOAST_MAX) {
      const dropped = this.hiddenIncomingToasts.shift();
      if (dropped) {
        if (dropped.hideTimer) clearTimeout(dropped.hideTimer);
        if (dropped.clearTimer) clearTimeout(dropped.clearTimer);
      }
    }
    const toast = { id: ++this._hiddenToastSeq, comment, visible: false, exiting: false, hideTimer: null as any, clearTimer: null as any };
    this.hiddenIncomingToasts.push(toast);
    this._win.setTimeout(() => {
      toast.visible = true;
    }, 20);
    toast.hideTimer = setTimeout(() => {
      // Fade out by moving upward. CSS reads .exiting to switch the fade-out transform from
      // "down and away" to "up and away". Keep .visible on so the transition runs against the
      // resting position rather than snapping back to the initial translateY(100%).
      toast.exiting = true;
      toast.clearTimer = setTimeout(() => {
        const i = this.hiddenIncomingToasts.indexOf(toast);
        if (i >= 0) this.hiddenIncomingToasts.splice(i, 1);
      }, this.HIDDEN_TOAST_FADE_MS);
    }, this.HIDDEN_TOAST_HOLD_MS);
  }

  private _dismissHiddenIncomingComments() {
    for (const t of this.hiddenIncomingToasts) {
      if (t.hideTimer) clearTimeout(t.hideTimer);
      if (t.clearTimer) clearTimeout(t.clearTimer);
    }
    this.hiddenIncomingToasts = [];
  }

  promptSignInForComments() {
    this.appService.promptSimpleSignup(SimpleSignupComponent).then(
      () => {},
      () => {}
    );
  }

  addComment(args) {
    if (!this.user) {
      // Never hard-redirect to login from the embed; the chat's own guest flow opens the
      // sign-in overlay instead.
      if (!this.embedMode) this.appService.redirectToConnect(true, false);
      return;
    }
    if (!this.user.firstName) {
      alert(this.translate.instant("video_player.profile_incomplete_for_comments"));
    } else {
      let msg: any = {
        message: args.message,
        author: {
          id: this.user.id,
          name: this.user.firstName + " " + this.user.lastName,
          photo: this.user.photoURL,
        },
        created: this.fbService.serverTimestamp(),
      };
      if (typeof msg.message === "string" && msg.message.indexOf("&nbsp;") !== -1) {
        msg.message = msg.message.replace("&nbsp;", " ");
      }
      msg.type = args.parentID ? "reply" : "post";
      if (args.parentID) {
        msg.parentID = args.parentID;
        if (args.parentMessage.author.id != this.user.id) {
          // dont send an email if youre replying to your own comment.
          let notifyArgs = {
            reply: msg.message,
            replyPhoto: msg.author.photo,
            replyUserName: this.user.firstName + " " + this.user.lastName,
            originalComment: args.parentMessage.message,
            originalUserID: args.parentMessage.author.id,
            videoTitle: this.video.title,
          };
          this.contentService.sendEmail(notifyArgs, "notify_comment_reply").subscribe(() => {});
        }
      }

      if (args.replyingToMessage) {
        if (args.replyingToMessage.author.id != this.user.id) {
          // dont send an email if youre replying to your own comment.
          let notifyArgs = {
            reply: msg.message,
            replyPhoto: msg.author.photo,
            replyUserName: this.user.firstName + " " + this.user.lastName,
            originalComment: args.replyingToMessage.message,
            originalUserID: args.replyingToMessage.author.id,
            videoTitle: this.video.title,
          };
          this.contentService.sendEmail(notifyArgs, "notify_comment_reply").subscribe(() => {});
        }
      }
      this._userJustSentComment = true;
      this.videoCommentsRef.add(msg);
    }
  }

  editComment(comment) {
    const modalRef = this.appService.showModal(EditCommentComponent, { size: "lg" }, true, true);
    modalRef.componentInstance.comment = comment;
    modalRef.result.then(
      (result) => {
        if (result) {
          this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${comment.key}`).update({ message: result.message, edited: true });
        }
      },
      (error) => {}
    );
  }

  deleteComment(msg) {
    if (confirm(this.translate.instant("video_player.confirm_delete_comment"))) {
      this.communityService.comments.forEach((item, index) => {
        if (msg.key == item.key) this.commentDeleteIndex = index;
      });
      this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${msg.key}`).delete();
    }
  }

  clearFlag(msg) {
    this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${msg.key}`).update({ cleared: true });
  }

  adminDeleteComment(msg) {
    if (confirm(this.translate.instant("video_player.confirm_delete_comment"))) {
      this.communityService.comments.forEach((item, index) => {
        if (msg.key == item.key) this.commentDeleteIndex = index;
      });
      this.fbService.doc(`video_comments/${this.videoConversationID}/comments/${msg.key}`).update({
        deleted: true,
      });
    }
  }

  ngOnChanges(changes) {
    if (changes && changes.videoID && changes.videoID.currentValue) {
      this.setupComments();
    } else if (changes && changes.vimeoID && changes.vimeoID.currentValue) {
      this.setupComments();
    } else if (changes && changes.videoURL && changes.videoURL.currentValue) {
    }
    // Feature flags can arrive after init (async event fetch) — rebuild the embed panel tabs.
    if (this.embedMode && changes && (changes.commentsEnabled || changes.shopEnabled || changes.signupEnabled || changes.signupConfig)) {
      this._updatePanelFeatures();
    }
  }

  ngOnDestroy() {
    this.updateProgress();
    this.communityService.comments = [];
    if (this.ytCurrentTimeInterval) this._win.clearInterval(this.ytCurrentTimeInterval);
    if (this.updateProgressInterval) this._win.clearInterval(this.updateProgressInterval);
    this._dismissHiddenIncomingComments();
    if (this._showCommentsPillCollapseTimer) clearTimeout(this._showCommentsPillCollapseTimer);
    document.removeEventListener("mousemove", this._boundOnMouseMove);
    document.removeEventListener("mouseup", this._boundOnMouseUp);
  }

  close(options?: { fromPopState?: boolean }) {
    if (this._closingPlayer) return;
    this._closingPlayer = true;

    const navIndex = this._videoNavIndex;
    this._videoNavIndex = -1;
    this.activeTime = undefined;
    this.selectedSection = "comments";
    this.selectedSectionWord = this.translate.instant("video_player.comments_tab");
    const callerElement = this.video?.ref_caller_element;
    if (this.ytCurrentTimeInterval) this._win.clearInterval(this.ytCurrentTimeInterval);
    if (this.updateProgressInterval) this._win.clearInterval(this.updateProgressInterval);
    this.updateProgress();
    this.document.body.classList.remove("video-open");
    this.clearVideoCommentsState();
    this.loadingComments = false;
    this.video = undefined;

    // Pop the whole in-player stack (A → B → close should leave the page before A).
    // When closing from Back that already left the stack, do not navigate again.
    if (!options?.fromPopState && navIndex >= 0) {
      history.go(-(navIndex + 1));
    } else if (!options?.fromPopState) {
      const params = new HttpParams().appendAll({});
      this._location.replaceState(location.pathname, params.toString());
    }

    if (callerElement?.nativeElement) {
      this._win.setTimeout(() => callerElement.nativeElement.focus(), 0);
    }
    this.eventBusService.emit(this.eventBusService.types.videoClosed, true);
    this.closed.next(true);
    this._win.setTimeout(() => {
      this._closingPlayer = false;
    }, 100);
  }

  minimizeVideoPlayer() {
    this.minimized = true;
    this.document.body.classList.remove("video-open");
  }

  maximizeVideoPlayer() {
    this.minimized = false;
    this.document.body.classList.add("video-open");
  }
}
